import React, { useMemo, useState, useEffect } from "react";

/**
 * Rankridge CMS Dashboard (React + TailwindCSS, JavaScript)
 * --------------------------------------------------------------------
 * - Pure JS (no TypeScript) single-file component
 * - Responsive grid, dark mode-ready
 * - Pass real data via props or it will use the demoData below
 * - Minimal dependencies (just React + Tailwind)
 * - Accessible, keyboard friendly
 *
 * Props:
 * {
 *   stats: [{ label, value, subLabel }]
 *   actions: [{ label, onClick }]
 *   drafts: [{ title, type, updatedAt, statusLabel, onEdit }]
 *   activities: [{ user, action, item, at }]
 *   schedule: [{ label, at }]
 *   media: [ { src, alt } ]
 * }
 */

const demoData = {
  stats: [
    { label: "Pages (Pub)", value: 98, subLabel: "Drafts 22" },
    { label: "Programs Live", value: 6, subLabel: "" },
    { label: "Blog (7d)", value: 3, subLabel: "30d 12" },
    { label: "Upcoming Events", value: 5, subLabel: "234.4 MB" },
  ],
  actions: [
    { label: "New Page" },
    { label: "New Blog Post" },
    { label: "New Program" },
    { label: "Add Slide" },
    { label: "Upload Image" },
    { label: "Edit Menus" },
  ],
  drafts: [
    { title: "Admissions Process", type: "Page", updatedAt: "2025-08-17", statusLabel: "Fage" },
    { title: "Olympiad Winners 2025", type: "Blog", updatedAt: "2025-08-16", statusLabel: "Edit" },
    { title: "NEET Success Strategies", type: "Blog", updatedAt: "2025-08-14", statusLabel: "Edit" },
  ],
  activities: [
    { user: "Alice", action: "published", item: "Admissions New Open", at: "2025-08-17T09:20:00" },
    { user: "Bob", action: "edited", item: "Olympiad Winners 2025", at: "2025-08-16T15:45:00" },
    { user: "Alice", action: "deleted", item: "Inquiry Form", at: "2025-08-16T11:10:00" },
  ],
  schedule: [
    { label: "Blog: NEET Success Strategies", at: "2025-08-21T10:00:00" },
    { label: "Carousel: Admissions Open 2025", at: "2025-08-22T09:00:00" },
  ],
  media: new Array(9).fill(0).map((_, i) => ({
    src: `https://picsum.photos/seed/rank-${i}/480/320`,
    alt: `Media ${i + 1}`,
  })),
};

// -------------------- Helpers --------------------
function classNames(...c) {
  return c.filter(Boolean).join(" ");
}

function formatDateTime(dtStr) {
  try {
    const d = new Date(dtStr);
    return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch (e) {
    return dtStr;
  }
}

function formatDate(dtStr) {
  try {
    const d = new Date(dtStr);
    return d.toLocaleDateString([], { dateStyle: "medium" });
  } catch (e) {
    return dtStr;
  }
}

// -------------------- UI Atoms --------------------
function Card({ className = "", children }) {
  return (
    <div className={classNames(
      "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition dark:border-slate-800 dark:bg-slate-900",
      className
    )}>
      {children}
    </div>
  );
}

function Stat({ label, value, subLabel }) {
  return (
    <Card className="p-5">
      <div className="text-slate-500 text-sm dark:text-slate-400">{label}</div>
      <div className="mt-1 text-4xl font-semibold tracking-tight">{value}</div>
      {subLabel ? (
        <div className="mt-1 text-slate-500 text-sm dark:text-slate-400">{subLabel}</div>
      ) : null}
    </Card>
  );
}

function PillButton({ children, onClick, className = "" }) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        "rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50 active:scale-[.99] dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800",
        className
      )}
    >
      {children}
    </button>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="rounded-md border px-1.5 py-0.5 text-[11px] font-medium text-slate-600 border-slate-300 dark:border-slate-700 dark:text-slate-300">
      {children}
    </kbd>
  );
}

// -------------------- Sections --------------------
function Drafts({ items }) {
  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Recent Drafts & Approvals</h3>
      </div>
      <ul className="divide-y divide-slate-200 dark:divide-slate-800">
        {items.map((d, i) => (
          <li key={i} className="flex items-center justify-between py-3">
            <div>
              <div className="font-medium hover:underline cursor-pointer">{d.title}</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                {d.type} · Updated {formatDate(d.updatedAt)}
              </div>
            </div>
            {d.statusLabel && (
              <button
                className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                onClick={d.onEdit}
              >
                {d.statusLabel}
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Activity({ items }) {
  return (
    <Card>
      <h3 className="mb-3 text-lg font-semibold">Recent Activity</h3>
      <ul className="divide-y divide-slate-200 dark:divide-slate-800">
        {items.map((a, i) => (
          <li key={i} className="flex items-start gap-3 py-3">
            <div className="mt-1 h-2.5 w-2.5 flex-none rounded-full bg-slate-400" />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold">{a.user}</span>
                <span className="text-slate-500 dark:text-slate-400">{a.action}</span>
                <span className="font-medium">{a.item}</span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{formatDateTime(a.at)}</div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Schedule({ items }) {
  return (
    <Card>
      <h3 className="mb-3 text-lg font-semibold">Publishing Schedule</h3>
      <div className="text-sm text-slate-500 -mt-1 mb-3 dark:text-slate-400">(Next 7–14 days)</div>
      <ul className="space-y-3">
        {items.map((s, i) => (
          <li key={i} className="flex items-start justify-between gap-4 rounded-xl border border-slate-100 p-3 dark:border-slate-800">
            <div className="flex items-start gap-3">
              <CalendarIcon className="mt-1 h-5 w-5 text-slate-400" />
              <div>
                <div className="font-medium">{s.label}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{formatDateTime(s.at)}</div>
              </div>
            </div>
            <PillButton className="px-3 py-1">View</PillButton>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function MediaGrid({ items }) {
  return (
    <Card>
      <h3 className="mb-3 text-lg font-semibold">Latest Media</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {items.map((m, i) => (
          <button key={i} className="group relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
            <img src={m.src} alt={m.alt || ""} className="h-28 w-full object-cover transition group-hover:scale-105" />
            <div className="pointer-events-none absolute inset-0 ring-0 ring-emerald-400/0 group-hover:ring-4 group-hover:ring-emerald-400/40" />
          </button>
        ))}
      </div>
    </Card>
  );
}

function SearchBar() {
  return (
    <div className="mb-4 flex items-center gap-2">
      <div className="relative w-full">
        <input
          type="text"
          placeholder="Search pages, posts, media…"
          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 pr-10 text-sm outline-none ring-0 placeholder:text-slate-400 focus:border-emerald-500 dark:border-slate-700 dark:bg-slate-900"
        />
        <svg
          viewBox="0 0 24 24"
          className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
          aria-hidden="true"
        >
          <path fill="currentColor" d="M10 4a6 6 0 1 1 0 12A6 6 0 0 1 10 4m10.32 15.9-3.9-3.9a8 8 0 1 0-1.41 1.41l3.9 3.9a1 1 0 0 0 1.41-1.41Z" />
        </svg>
      </div>
      <div className="hidden items-center gap-1 rounded-xl border border-slate-300 px-2 py-1 text-xs text-slate-600 sm:flex dark:border-slate-700 dark:text-slate-300">
        <span className="hidden sm:inline">Shortcut</span>
        <Kbd>Ctrl</Kbd>
        <span>+</span>
        <Kbd>K</Kbd>
      </div>
    </div>
  );
}

function Header({ stats, actions }) {
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <div className="flex items-center gap-2">
          <PillButton className="hidden sm:inline-flex">Invite</PillButton>
          <PillButton className="inline-flex bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600">Publish</PillButton>
        </div>
      </div>
      <SearchBar />
      {/* <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <Stat key={i} {...s} />
        ))}
      </div> */}
      <div className="mt-4 flex flex-wrap gap-2">
        {actions.map((a, i) => (
          <PillButton key={i} onClick={a.onClick}>{a.label}</PillButton>
        ))}
      </div>
    </div>
  );
}

// -------------------- Main Component --------------------
export default function RankridgeCmsDashboard(props) {
  const data = { ...demoData, ...props };
  const [now, setNow] = useState(Date.now());

  // example ticker to update relative times if you add them later
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const leftCol = (
    <div className="grid gap-4">
      <Drafts items={data.drafts} />
      <Schedule items={data.schedule} />
      <Card>
        <h3 className="mb-2 text-lg font-semibold">Latest Permingle</h3>
        <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3 text-sm dark:border-slate-800">
          <div>
            <div className="font-medium">Mlog</div>
            <div className="text-slate-500 dark:text-slate-400">Audit</div>
          </div>
          <div className="text-slate-600 dark:text-slate-300">88/2025</div>
        </div>
      </Card>
    </div>
  );

  const rightCol = (
    <div className="grid gap-4">
      <Activity items={data.activities} />
      <MediaGrid items={data.media} />
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-slate-50 p-4 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:p-6">
      <div className="mx-auto max-w-7xl">
        <Header stats={data.stats} actions={data.actions} />

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {/* Left span 2 cols on xl */}
          <div className="xl:col-span-2">{leftCol}</div>
          {/* Right */}
          <div className="xl:col-span-1">{rightCol}</div>
        </div>
      </div>
    </div>
  );
}

// -------------------- Icons --------------------
function CalendarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M7 2v3M17 2v3M3 9h18M4 6h16a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
